import { Close } from "@mui/icons-material";
import {
	Card,
	CardContent,
	CardHeader,
	Grid,
	IconButton,
	styled,
	TextField,
	Typography,
} from "@mui/material";
import { api, types } from "api";
import { InfoSnackBar } from "components/InfoSnackBar";
import { StyledButton } from "components/StyledButton";
import { UserSearch } from "components/UserSearch";
import { CREATE_GROUP_ERROR, GROUP_NAME_NO_EMPTY } from "messages";
import { useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { routes } from "routes";

const PREFIX = "CreateGroupPage";

const classes = {
	root: `${PREFIX}-root`,
	title: `${PREFIX}-title`,
	content: `${PREFIX}-content`,
	groupName: `${PREFIX}-groupName`,
	buttonGroup: `${PREFIX}-buttonGroup`,
};

const Root = styled("div")(({ theme }) => ({
	[`&.${classes.root}`]: {},

	[`& .${classes.title}`]: {
		marginLeft: theme.spacing(2),
		marginTop: "2rem",
	},

	[`& .${classes.content}`]: {
		padding: theme.spacing(5),
	},

	[`& .${classes.groupName}`]: {
		alignItems: "center",
		marginBottom: "2rem",
	},

	[`& .${classes.buttonGroup}`]: {
		display: "flex",
		justifyContent: "space-between",
		marginTop: "3.75rem",
		minWidth: "fit-content",
		width: "200px",
	},
}));

export interface CreateGroupPageProps {
	toggleGroupsFresh: () => void;
}

export const CreateGroupPage = ({
	toggleGroupsFresh,
}: CreateGroupPageProps): JSX.Element => {
	const [groupName, setGroupName] = useState("");
	const [addStudentIndicator, setAddStudentIndicator] = useState(false);
	const [isShowEmptyIndicator, setIsShowEmptyIndicator] = useState(false);
	const [createErr, setCreateErr] = useState(false);
	const [users, setUsers] = useState<types.User[]>([]);
	const roleProp: types.RoleType = "student";
	const onCloseUserSearch = (): void => {
		setAddStudentIndicator(false);
	};

	const history = useHistory();

	const onConfirm = (): void => {
		if (groupName.length === 0) {
			setIsShowEmptyIndicator(true);
			return;
		}

		onCreatingGroup()
			.then(() => {
				toggleGroupsFresh();
				history.push(routes._.groups.path);
			})
			.catch(() => {
				setCreateErr(true);
			});
	};

	const onCreatingGroup = async (): Promise<void> => {
		try {
			const res = await api.addGroup({
				name: groupName,
				students: users.map((u) => u.userId),
			});
			if (res.status !== 201) {
				throw new Error(res.data || "Failed to create group");
			}
		} catch (e) {
			setCreateErr(true);
		}
	};

	return (
		<Root className={classes.root}>
			<InfoSnackBar
				isOpen={isShowEmptyIndicator}
				severity="error"
				onClose={() => {
					setIsShowEmptyIndicator(false);
				}}
				message={GROUP_NAME_NO_EMPTY}
			/>
			<InfoSnackBar
				isOpen={createErr}
				severity="error"
				onClose={() => {
					setCreateErr(false);
				}}
				message={CREATE_GROUP_ERROR}
			/>
			<Card>
				<CardContent>
					<Typography variant="h4" className={classes.title}>
						New Group
					</Typography>
					<div className={classes.content}>
						<Grid container spacing={4} className={classes.groupName}>
							<Grid item xs={4}>
								<TextField
									id="group-name"
									label="Group Name"
									required
									error={isShowEmptyIndicator}
									variant="outlined"
									value={groupName}
									fullWidth
									onChange={(e) => {
										setGroupName(e.target.value);
										setIsShowEmptyIndicator(false);
									}}
									helperText={isShowEmptyIndicator ? GROUP_NAME_NO_EMPTY : ""}
								/>
							</Grid>
							{!addStudentIndicator && (
								<Grid item xs={2}>
									<StyledButton
										color="primary"
										variant="contained"
										size="medium"
										onClick={() => {
											setAddStudentIndicator(true);
										}}
										data-testid="add-student-btn"
									>
										Add student
									</StyledButton>
								</Grid>
							)}
						</Grid>
						{addStudentIndicator && (
							<Card className={classes.content}>
								<CardHeader
									action={
										<IconButton
											aria-label="settings"
											onClick={onCloseUserSearch}
											size="large"
										>
											<Close />
										</IconButton>
									}
								/>
								<UserSearch role={roleProp} users={users} setUsers={setUsers} />
							</Card>
						)}
						<div className={classes.buttonGroup}>
							<StyledButton
								variant="contained"
								component={Link}
								to={routes._.groups.path}
								color="secondary"
							>
								Cancel
							</StyledButton>
							<StyledButton
								color="primary"
								variant="contained"
								onClick={onConfirm}
							>
								Confirm
							</StyledButton>
						</div>
					</div>
				</CardContent>
			</Card>
		</Root>
	);
};
